import { SupportReport } from "./buildSupportReport";

// A channel is a pure function from a report to a URL. Adding one is a new
// entry here and nothing else -- no component change, no branching at the
// call site -- which is what "channel agnostic" has to mean to be worth the
// indirection. Ruled 2026-08-31: ship Zoho AND WhatsApp, together.
export type SupportChannelId = "zoho" | "whatsapp";

export type SupportChannelConfig = {
  // Zoho Desk form URL. Absent config disables the channel rather than
  // rendering a control that goes nowhere.
  zohoUrl?: string;
  // Digits only, no "+" -- wa.me rejects the plus.
  whatsappNumber?: string;
};

export type SupportChannel = {
  id: SupportChannelId;
  href: string;
};

const encode = (value: string) => encodeURIComponent(value);

/*
 * ⚠ ASSUMED, NOT VERIFIED. These are Zoho Desk's documented prefill parameter
 * names; this portal's Zoho instance has not been exercised, so a renamed or
 * unmapped field silently yields an EMPTY ticket that looks fine to whoever
 * sent it. Ruled 2026-08-31 to proceed on the assumption and settle it in
 * integration tests -- TODO-234. Do not read a rendered link as proof.
 */
const zohoHref = (base: string, report: SupportReport): string => {
  const separator = base.includes("?") ? "&" : "?";
  const params = [
    `subject=${encode(report.subject)}`,
    `description=${encode(report.body)}`,
  ];

  return `${base}${separator}${params.join("&")}`;
};

// wa.me carries the whole report as message text: WhatsApp has no structured
// fields, which is why buildSupportReport renders prose from the same fields
// Zoho receives rather than the two being authored separately.
const whatsappHref = (number: string, report: SupportReport): string =>
  `https://wa.me/${number.replace(/\D/g, "")}?text=${encode(report.body)}`;

// Order is the order they are offered. Zoho first: it produces a tracked
// ticket with an owner, and WhatsApp is the low-friction path for a payer who
// will not fill a form. Both are offered together rather than one being a
// fallback for the other -- a payer chooses, the app does not choose for them.
export const getSupportChannels = (
  report: SupportReport,
  config: SupportChannelConfig
): SupportChannel[] => {
  const channels: SupportChannel[] = [];

  if (config.zohoUrl) {
    channels.push({ id: "zoho", href: zohoHref(config.zohoUrl, report) });
  }

  if (config.whatsappNumber && /\d/.test(config.whatsappNumber)) {
    channels.push({
      id: "whatsapp",
      href: whatsappHref(config.whatsappNumber, report),
    });
  }

  return channels;
};

export default getSupportChannels;
